/**
 * Cart fetch — section-rendering aware cart API wrappers.
 *
 * Each function POSTs JSON to the matching `/cart/*.js` endpoint with the
 * `sections` + `sections_url` extension. Shopify replies with the cart JSON
 * plus a `sections` map of pre-rendered HTML; we dispatch `cart:update` so
 * `<cart-items>` and `<cart-icon-bubble>` can swap their own innerHTML.
 *
 * One round-trip per cart mutation. No client-side templating, no jQuery,
 * no `window.Shopify.*` indirection from first-party code.
 *
 * Consumers (Phase 12):
 *   - `<line-item>` — quantity change / remove
 *   - `<add-item-form>` — post-ATC cart refresh + hemming update
 *
 * Source path: theme/js/core/cart-fetch.esm.js
 * Built name : ./cart-fetch.js
 *
 * AI: Entirely written by AI
 * Date: 2026-05-07
 * Model: Claude Opus 4.7
 * Updated: 2026-05-07 (Claude Opus 4.7) — section constants moved to
 *   core/sections.esm.js; refreshCart removed (addToCart appends sections
 *   to FormData, so a follow-up no-mutation POST is unnecessary).
 * Updated: 2026-05-08 (Claude Opus 4.7) — serialize all cart mutations
 *   through a single FIFO promise tail so concurrent quantity-button
 *   clicks resolve in submission order.
 *
 * [Cart]
 */

import { log } from './log.js';
import { parseCartStateFromSectionHtml } from './cart-state.js';
import { SECTION_CART_ITEMS, SECTION_CART_ICON_BUBBLE } from './sections.js';

const SECTIONS = [SECTION_CART_ITEMS, SECTION_CART_ICON_BUBBLE];
const SECTIONS_URL = '/cart';

// Serialize all cart mutations through a single in-flight tail. Rapid
// quantity-button clicks on a `<line-item>` previously fired concurrent
// /cart/change.js requests; whichever response landed second won the
// `cart:update` race regardless of intended order. The queue chains each
// mutation onto the previous tail so responses arrive in submission order
// — the cart visibly steps through intermediate states, which is the
// honest UX (each click reflects the cart at that moment).
let _tail = Promise.resolve();
function serialize(run) {
  const next = _tail.then(run, run);
  // Don't let a rejection poison the queue.
  _tail = next.then(noop, noop);
  return next;
}
function noop() {}

async function postJson(url, body) {
  return post(url, {
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

async function post(url, { headers = {}, body }) {
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...headers,
    },
    body,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.description || data.message || response.statusText);
    err.status = response.status;
    err.response = data;
    throw err;
  }
  return data;
}

function dispatchCartUpdate(data) {
  const sectionHtml = data.sections && data.sections[SECTION_CART_ICON_BUBBLE];
  const cart = parseCartStateFromSectionHtml(sectionHtml) || data;
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: false,
      detail: { cart, sections: data.sections || {} },
    })
  );
}

export async function changeLine(line, quantity) {
  return serialize(async () => {
    log('[Cart] changeLine', { line, quantity });
    const data = await postJson('/cart/change.js', {
      line,
      quantity,
      sections: SECTIONS,
      sections_url: SECTIONS_URL,
    });
    dispatchCartUpdate(data);
    return data;
  });
}

export async function updateItems(updates) {
  return serialize(async () => {
    log('[Cart] updateItems', updates);
    const data = await postJson('/cart/update.js', {
      updates,
      sections: SECTIONS,
      sections_url: SECTIONS_URL,
    });
    dispatchCartUpdate(data);
    return data;
  });
}

export async function clearCart() {
  return serialize(async () => {
    log('[Cart] clearCart');
    const data = await postJson('/cart/clear.js', {
      sections: SECTIONS,
      sections_url: SECTIONS_URL,
    });
    dispatchCartUpdate(data);
    return data;
  });
}

// POST a `<form>` (or a pre-built FormData) to /cart/add.js. FormData is
// required so the existing form contract (multi-value option inputs,
// line-item properties like `properties[Hem]`) carries through unchanged.
// Sections are appended to the FormData so the response includes rendered
// HTML; dispatchCartUpdate parses cart state from the cart-icon-bubble
// section's <script data-cart-state> block — one round-trip, no follow-up
// refreshCart() needed for the non-hemming path.
export async function addToCart(formOrFormData) {
  // Build FormData synchronously — outside the queue — so the form's
  // current input values are captured at call time, not whenever the
  // tail flushes.
  const fd =
    formOrFormData instanceof FormData ? formOrFormData : new FormData(formOrFormData);
  fd.append('sections', SECTIONS.join(','));
  fd.append('sections_url', SECTIONS_URL);
  return serialize(async () => {
    log('[Cart] addToCart');
    const data = await post('/cart/add.js', { body: fd });
    dispatchCartUpdate(data);
    return data;
  });
}

log('[Cart] loaded');
